import { inject, Injectable } from '@angular/core';
import find from 'lodash-es/find';
import map from 'lodash-es/map';
import sortBy from 'lodash-es/sortBy';
import flatten from 'lodash-es/flatten';
import groupBy from 'lodash-es/groupBy';
import compact from 'lodash-es/compact';
import mapValues from 'lodash-es/mapValues';

import { GoogleMaps } from '../google-maps.namespace';

import { IAddressData } from '@df/session/api/address-book/address';
import { DfApiConfigService } from '@df/api/config/df-api-config.service';
import { AddressFieldsetComponentDef } from '@df/ui/form/address-fieldset.component.def';

@Injectable()
export class GoogleMapsAddressService {
    private dfApiConfigService = inject(DfApiConfigService);

    private autocomplete?: google.maps.places.Autocomplete;

    private countryCode: string | undefined;

    createAutocomplete(element: HTMLInputElement, addressFieldsetComponent: AddressFieldsetComponentDef, countryCode?: string) {
        if (typeof window === 'undefined') {
            return undefined;
        }
        if (window.google?.maps?.places) {
            const options: google.maps.places.AutocompleteOptions = { types: ['geocode'] };
            if (countryCode) {
                options.componentRestrictions = { country: countryCode.toLowerCase() };
                this.countryCode = countryCode;
            }
            const autocomplete = new google.maps.places.Autocomplete(element, options);
            autocomplete.addListener('place_changed', () => {
                this.onPlaceChanged(autocomplete.getPlace(), addressFieldsetComponent);
            });
            this.autocomplete = autocomplete;
        }
    }

    setCountryCode(countryCode: string | undefined) {
        if (countryCode !== this.countryCode) {
            this.countryCode = countryCode;
            if (this.autocomplete && this.countryCode) {
                this.autocomplete.setComponentRestrictions({ country: this.countryCode.toLowerCase() });
            }
        }
    }

    private onPlaceChanged(place: google.maps.places.PlaceResult, addressFieldsetComponent: AddressFieldsetComponentDef) {
        if (addressFieldsetComponent?.data) {
            const addressData = this.getAddressDataForPlace(place);
            if (addressData) {
                this.applyAddressData(addressData, addressFieldsetComponent);
            }
        }
    }

    protected applyAddressData(addressData: IAddressData, addressFieldsetComponent: AddressFieldsetComponentDef) {
        GoogleMaps.Address.FIELDS.forEach(field => {
            addressFieldsetComponent.data[field] = addressData[field];
        });
        addressFieldsetComponent.detectChanges();
    }

    private getAddressDataForPlace(place: google.maps.places.PlaceResult): IAddressData | undefined {
        if (place.address_components) {
            //  I am not sure what that is for, but it is copy from core2
            const subremiseComponent = find(place.address_components, i => i.types?.indexOf('subpremise') > -1);

            if (subremiseComponent) {
                const streetNumberComponent = find(place.address_components, i => i.types?.indexOf('street_number') > -1);

                if (streetNumberComponent) {
                    streetNumberComponent.long_name = subremiseComponent.long_name + '/' + subremiseComponent.long_name;
                    streetNumberComponent.short_name = subremiseComponent.short_name + '/' + subremiseComponent.short_name;
                }
            }
            // ----

            const data: { [name: string]: string | number } = mapValues(
                groupBy(compact(flatten(map(place.address_components, component => this.normalizeAddressComponent(component)))), 'name'),
                group => map(sortBy(group, 'order'), 'value').join(' ')
            );

            const countries = this.dfApiConfigService.countries;
            if (countries) {
                if (data['country_id'] && (data['region'] || data['region_id'])) {
                    const country = countries.find(country => country.code?.toLowerCase() === data['country_id']?.toString().toLowerCase());
                    if (country?.regions) {
                        const region = find(
                            country.regions,
                            region =>
                                (!!data['region_id'] && region.code === data['region_id']) ||
                                (!!data['region'] && region.name?.toLowerCase() === data['region']?.toString().toLowerCase())
                        );
                        if (region) {
                            data['region_id'] = region.id;
                            data['region'] = region.name;
                        }
                    }
                }
            }
            return data;
        }
    }

    private normalizeAddressComponent(
        component: google.maps.GeocoderAddressComponent
    ): GoogleMaps.Address.IEntryData | GoogleMaps.Address.IEntryData[] | undefined {
        const type = component.types[0];
        const short = component.short_name;
        const long = component.long_name;
        switch (type) {
            case 'premise':
                return {
                    name: 'street_2',
                    value: long,
                    order: 1
                };
            case 'locality':
                return {
                    name: 'city',
                    value: long,
                    order: 1
                };
            case 'colloquial_area':
                return {
                    name: 'region',
                    value: long,
                    order: 1
                };
            case 'administrative_area_level_1':
                return [
                    {
                        name: 'region_id',
                        value: short,
                        order: 1
                    },
                    {
                        name: 'region',
                        value: long,
                        order: 1
                    }
                ];
            case 'administrative_area_level_2':
                break;
            //     return {
            //         name : 'region',
            //         value : long,
            //         order : 2
            //     };
            case 'postal_town':
                return {
                    name: 'region',
                    value: long,
                    order: 3
                };
            case 'country':
                return {
                    name: 'country_id',
                    value: short,
                    order: 1
                };
            case 'postal_code':
                return {
                    name: 'postcode',
                    value: long,
                    order: 1
                };
            case 'route':
                return {
                    name: 'street_2',
                    value: long,
                    order: 10
                };
            case 'street_number':
                return {
                    name: 'street_1',
                    value: long,
                    order: 11
                };
            case 'neighborhood':
                // skip these fields
                break;
        }

        return undefined;
    }
}
